import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

type InactivityContextAPI = {
    userIsActive: boolean;
};
export const InactivityContext = createContext<InactivityContextAPI | undefined>(undefined);
export function useInactivityContext(): InactivityContextAPI {
    const ctx = useContext(InactivityContext);
    if (!ctx) {
        throw new Error('usePuzzleIndex must be used within a PuzzleIndexContext.Provider');
    }
    return ctx;
}

const INACTIVITY_TIME = 120 * 1000; // 120 seconds
export function InactivityContextProvider({ children }: { children: React.ReactNode }) {
    const inactivityTimeout = useRef<NodeJS.Timeout | null>(null);
    const [userIsActive, setUserIsActive] = useState<boolean>(true);

    const resetInactivityTimer = useCallback(() => {
        setUserIsActive(true);
        if (inactivityTimeout.current) {
            clearTimeout(inactivityTimeout.current);
        }
        inactivityTimeout.current = setTimeout(() => {
            console.log('User is now inactive');
            setUserIsActive(false);
        }, INACTIVITY_TIME);
    }, []);

    const value: InactivityContextAPI = useMemo(() => ({ userIsActive }), [userIsActive]);

    useEffect(() => {
        document.addEventListener('mousemove', resetInactivityTimer);
        document.addEventListener('click', resetInactivityTimer);
        document.addEventListener('keypress', resetInactivityTimer);

        resetInactivityTimer();

        return () => {
            document.removeEventListener('mousemove', resetInactivityTimer);
            document.removeEventListener('click', resetInactivityTimer);
            document.removeEventListener('keypress', resetInactivityTimer);
        };
    }, [resetInactivityTimer]);

    return <InactivityContext.Provider value={value}>{children}</InactivityContext.Provider>;
}
